import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Modal, Tabs } from "antd";
import { Tooltip } from "@mui/material";
import secureLocalStorage from "react-secure-storage";
import "./style.css";
import Apps from "./Apps";
import UserPage from "../user page/UserInfo";
import ChangePassword from "../user page/ChangePassword";
import TwoFactorSettings from "../twoFactor/TwoFactorSettings";
import IdleSignOut from "../ui/IdleSignOut";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { IDLE_SIGNED_OUT_KEY, pageTitle } from "../brand";
import { signOut } from "../Api/index";
const WORKSPACE_TABS_KEY = "workspace_tabs_v1";
const WORKSPACE_ACTIVE_KEY = "workspace_active_tab_v1";

function SystemFrame({ src, title }) {
  return (
    <div className="system-frame-shell">
      <iframe src={src} title={title} className="system-frame-iframe" />
    </div>
  );
}

function SettingsPage({ children }) {
  return <div className="portal-page">{children}</div>;
}

export default function UserManagement() {
  const [activeKey, setActiveKey] = useState(() => {
    return secureLocalStorage.getItem(WORKSPACE_ACTIVE_KEY) || "1";
  });

  const [dynamicTabs, setDynamicTabs] = useState(() => {
    const saved = secureLocalStorage.getItem(WORKSPACE_TABS_KEY);
    return Array.isArray(saved) ? saved : [];
  });

  const baseItems = useMemo(
    () => [
      {
        key: "1",
        label: (
          <span className="home-entry-tab-label">
            <AppsRoundedIcon fontSize="small" />
            <span>Systems</span>
          </span>
        ),
        children: <Apps onOpenSystem={openSystemTab} />,
        closable: false,
      },
      {
        key: "2",
        label: (
          <span className="home-entry-tab-label">
            <PersonOutlineRoundedIcon fontSize="small" />
            <span>My Profile</span>
          </span>
        ),
        children: (
          <SettingsPage>
            <Helmet>
              <title>{pageTitle("My Profile")}</title>
            </Helmet>
            <UserPage />
          </SettingsPage>
        ),
        closable: false,
      },
      {
        key: "3",
        label: (
          <span className="home-entry-tab-label">
            <LockOutlinedIcon fontSize="small" />
            <span>Security</span>
          </span>
        ),
        children: (
          <SettingsPage>
            <Helmet>
              <title>{pageTitle("Security")}</title>
            </Helmet>
            <ChangePassword />
            <TwoFactorSettings />
          </SettingsPage>
        ),
        closable: false,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  function openSystemTab(tab) {
    if (!tab?.key || !tab?.name || !tab?.to) return;

    const tabKey = `sys-${tab.key}`;

    setDynamicTabs((prev) => {
      const exists = prev.some((item) => item.key === tabKey);
      if (exists) return prev;

      return [
        ...prev,
        {
          key: tabKey,
          name: tab.name,
          url: tab.to,
        },
      ];
    });

    setActiveKey(tabKey);
  }

  const dynamicLabel = (tab) => (
    <span className="home-entry-tab-label">
      <span>{tab.name}</span>
    </span>
  );

  const removeTab = (targetKey) => {
    const allTabs = [...baseItems, ...dynamicTabs];
    const currentIndex = allTabs.findIndex((item) => item.key === targetKey);

    const nextTabs = dynamicTabs.filter((item) => item.key !== targetKey);
    setDynamicTabs(nextTabs);

    if (activeKey !== targetKey) return;

    const nextAllTabs = [...baseItems, ...nextTabs];
    const fallback =
      nextAllTabs[currentIndex - 1] ||
      nextAllTabs[currentIndex] ||
      nextAllTabs[0];

    setActiveKey(fallback?.key || "1");
  };

  const endSession = useCallback(async (reason) => {
    // Ends the shared login, so the other apps are logged out too
    await signOut().catch(() => {});
    secureLocalStorage.removeItem(WORKSPACE_TABS_KEY);
    secureLocalStorage.removeItem(WORKSPACE_ACTIVE_KEY);
    secureLocalStorage.removeItem("profile");
    if (reason === "idle") {
      try {
        sessionStorage.setItem(IDLE_SIGNED_OUT_KEY, "1");
      } catch (error) {
        // storage blocked: the login page just won't say why
      }
    }
    window.location.href = "/";
  }, []);

  const signOutForIdle = useCallback(() => endSession("idle"), [endSession]);

  const handleLogout = () => {
    Modal.confirm({
      title: "Sign out?",
      content:
        "This signs you out of the CBX Suite and every system opened from it.",
      okText: "Sign out",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: () => endSession(),
    });
  };

  const dynamicTabItems = dynamicTabs.map((tab) => ({
    key: tab.key,
    label: dynamicLabel(tab),
    children: <SystemFrame src={tab.url} title={tab.name} />,
    closable: true,
  }));

  const items = [...baseItems, ...dynamicTabItems];
  const activeSystem = dynamicTabs.find((tab) => tab.key === activeKey);

  useEffect(() => {
    secureLocalStorage.setItem(WORKSPACE_TABS_KEY, dynamicTabs);
  }, [dynamicTabs]);

  useEffect(() => {
    const existsInBase = baseItems.some((item) => item.key === activeKey);
    const existsInDynamic = dynamicTabs.some((item) => item.key === activeKey);

    if (!existsInBase && !existsInDynamic) {
      setActiveKey("1");
      return;
    }

    secureLocalStorage.setItem(WORKSPACE_ACTIVE_KEY, activeKey);
  }, [activeKey, dynamicTabs, baseItems]);

  return (
    <div className="home-entry-root">
      <Helmet>
        <title>
          {activeSystem ? pageTitle(activeSystem.name) : pageTitle()}
        </title>
      </Helmet>

      <IdleSignOut onSignOut={signOutForIdle} />


      <main className="home-entry-main">
        <Tabs
          type="editable-card"
          hideAdd
          activeKey={activeKey}
          onChange={setActiveKey}
          items={items}
          className="home-entry-tabs"
          animated={false}
          destroyOnHidden={false}
          tabBarExtraContent={{
            right: (
              <div className="portal-tab-actions">
                {activeSystem && (
                  <Tooltip title="Open this system in its own browser tab">
                    <a
                      className="portal-popout"
                      href={activeSystem.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewRoundedIcon fontSize="small" />
                      <span>Open in new tab</span>
                    </a>
                  </Tooltip>
                )}
                <button
                  type="button"
                  className="portal-signout"
                  onClick={handleLogout}
                  aria-label="Sign out"
                >
                  <LogoutRoundedIcon fontSize="small" />
                  <span>Sign out</span>
                </button>
              </div>
            ),
          }}
          onEdit={(targetKey, action) => {
            if (action === "remove") removeTab(targetKey);
          }}
        />
      </main>
    </div>
  );
}
